import { useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, TextField, Typography,
    Stack, Table, TableHead, TableBody, TableRow, TableCell,
    FormControl, Select, MenuItem
} from '@mui/material';
import * as api from './orderTypesApi';
import type { OrderType, Language, PrintDocumentTemplate, DeliveryPlanningTemplate } from './orderTypesApi';
import { useDetailView } from '../common/useDetailView';
import { sx13, FormRow, DetailViewShell, mergeLocalizations } from '../common/detailView';

interface Props {
    orderTypeId: number | null;
}

export function OrderTypeView({ orderTypeId }: Props) {
    const { t } = useTranslation();
    const detail = useDetailView<OrderType>();

    const [languages, setLanguages] = useState<Language[]>([]);
    const [templates, setTemplates] = useState<PrintDocumentTemplate[]>([]);
    const [dpTemplates, setDpTemplates] = useState<DeliveryPlanningTemplate[]>([]);
    const [dpEnabled, setDpEnabled] = useState(false);

    const buildDefaultLoc = (lang: Language) => ({ language: lang });

    const load = useCallback(async () => {
        await detail.withLoading(async () => {
            const [langs, tpls, dpResp, enabled] = await Promise.all([
                api.getLanguages(),
                api.getPrintDocumentTemplates(),
                api.getDeliveryPlanningTemplates(),
                api.isDeliveryPlanningEnabled(),
            ]);
            setLanguages(langs);
            setTemplates(tpls);
            setDpTemplates(dpResp?.deliveryPlanningTemplates ?? []);
            setDpEnabled(enabled === true);

            if (orderTypeId === null) {
                detail.setItem({
                    id: 0,
                    orderTypeName: '',
                    printDocumentTemplate: null,
                    pickingListDocumentTemplate: null,
                    deliveryPlanningTemplate: null,
                    localizations: mergeLocalizations([], langs, buildDefaultLoc)
                });
                detail.setIsNew(true);
                detail.setIsEditing(true);
            } else {
                const data = await api.getOrderType(orderTypeId);
                data.localizations = mergeLocalizations(data.localizations ?? [], langs, buildDefaultLoc);
                detail.setItem(data);
                detail.setIsNew(false);
                detail.setIsEditing(false);
            }
        });
    }, [orderTypeId]);

    useEffect(() => { load(); }, [load]);

    const item = detail.item;

    const updateLocalization = (languageId: number, name: string) => {
        if (!item) return;
        detail.update('localizations', item.localizations.map(loc =>
            loc.language.id === languageId ? { ...loc, name } : loc
        ));
    };

    const renderTemplateSelect = (
        value: PrintDocumentTemplate | null,
        onSelect: (tpl: PrintDocumentTemplate | null) => void
    ) => (
        <FormControl size="small" fullWidth>
            <Select
                value={value?.id ?? ''}
                onChange={e => {
                    const tpl = templates.find(x => x.id === e.target.value);
                    onSelect(tpl ?? null);
                }}
            >
                {[...templates]
                    .sort((a, b) => a.id - b.id)
                    .map(tpl => (
                        <MenuItem key={tpl.id} value={tpl.id}>{tpl.description}</MenuItem>
                    ))}
            </Select>
        </FormControl>
    );

    const save = async () => {
        if (!item) return;
        await detail.withSaving(async () => {
            const payload: OrderType = { ...item };
            if (dpEnabled) {
                const dpId = payload.deliveryPlanningTemplate?.id ?? 0;
                payload.deliveryPlanningTemplateId = dpId > 0 ? dpId : null;
            } else {
                payload.deliveryPlanningTemplateId = null;
            }
            const response = detail.isNew
                ? await api.createOrderType(payload)
                : await api.updateOrderType(payload);
            response.localizations = mergeLocalizations(response.localizations ?? [], languages, buildDefaultLoc);
            detail.setItem(response);
            detail.setIsNew(false);
            detail.setIsEditing(false);
        });
    };

    return (
        <DetailViewShell<OrderType>
            featureKey="devFeature_reactOrderTypeView"
            title={item?.orderTypeName ?? ''}
            isEditing={detail.isEditing}
            isNew={detail.isNew}
            saving={detail.saving}
            canSave
            loading={detail.loading}
            error={detail.error}
            item={item}
            onEdit={detail.startEdit}
            onSave={save}
            onCancel={detail.cancelEdit}
            onClearError={() => detail.setError(null)}
        >
            {(orderType) => {
                const sortedLocs = [...orderType.localizations].sort((a, b) =>
                    (a.language.name ?? '').localeCompare(b.language.name ?? '')
                );

                return (<>
                    <FormRow label={t('ordertype.orderTypeName')}>
                        <Typography sx={sx13}>{orderType.orderTypeName ? t(orderType.orderTypeName) : ''}</Typography>
                    </FormRow>

                    <FormRow label={t('ordertype.printDocumentTemplate')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx13}>{orderType.printDocumentTemplate?.description}</Typography>
                        ) : (
                            renderTemplateSelect(orderType.printDocumentTemplate, v => detail.update('printDocumentTemplate', v))
                        )}
                    </FormRow>

                    <FormRow label={t('ordertype.pickingListDocumentTemplate')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx13}>{orderType.pickingListDocumentTemplate?.description}</Typography>
                        ) : (
                            renderTemplateSelect(orderType.pickingListDocumentTemplate, v => detail.update('pickingListDocumentTemplate', v))
                        )}
                    </FormRow>

                    {dpEnabled && (
                        <FormRow label={t('ordertype.deliveryPlanningTemplate')}>
                            {!detail.isEditing ? (
                                <Typography sx={sx13}>{orderType.deliveryPlanningTemplate?.deliveryPlanningTemplateName}</Typography>
                            ) : (
                                <FormControl size="small" fullWidth>
                                    <Select
                                        value={orderType.deliveryPlanningTemplate?.id ?? ''}
                                        onChange={e => {
                                            const dpt = dpTemplates.find(x => x.id === e.target.value);
                                            detail.update('deliveryPlanningTemplate', dpt ?? null);
                                        }}
                                    >
                                        {[...dpTemplates]
                                            .sort((a, b) => a.deliveryPlanningTemplateName.localeCompare(b.deliveryPlanningTemplateName))
                                            .map(dpt => (
                                                <MenuItem key={dpt.id} value={dpt.id}>{dpt.deliveryPlanningTemplateName}</MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            )}
                        </FormRow>
                    )}

                    <Box />

                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ ...sx13, fontWeight: 'bold' }}>{t('common.language')}</TableCell>
                                <TableCell sx={{ ...sx13, fontWeight: 'bold' }}>{t('ordertype.orderTypeName')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedLocs.map(loc => (
                                <TableRow key={loc.language.id}>
                                    <TableCell sx={{ ...sx13, width: 150 }}>{loc.language.name}</TableCell>
                                    <TableCell>
                                        {!detail.isEditing ? (
                                            <Typography sx={sx13}>{loc.name}</Typography>
                                        ) : (
                                            <TextField size="small" fullWidth inputProps={{ maxLength: 100 }}
                                                value={loc.name ?? ''}
                                                onChange={e => updateLocalization(loc.language.id, e.target.value)} />
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </>);
            }}
        </DetailViewShell>
    );
}
